import type { Device } from "@/devices/hooks/use-devices";

import type { AppLanguage } from "@/localization/hooks/use-translation";

const PLATFORM_NAMES: Record<string, string> = {
  darwin: "macOS",
  linux: "Linux",
  win32: "Windows",
};

/**
 * The `process.platform` code the desktop appended to its own activation label.
 *
 * It writes the label as `hostname (win32)`, so the row would otherwise read
 * "DESKTOP-3RADFG4 (win32) · Windows" — the platform twice, once in Node's
 * spelling. Only a code this application has a name for is treated as one, so a
 * machine genuinely called "Studio (basement)" keeps its name.
 */
const PLATFORM_LABEL_SUFFIX = /\s*\(([^()]+)\)$/;

const readLabelPlatform = (label: string) => {
  const suffix = PLATFORM_LABEL_SUFFIX.exec(label);
  const code = suffix?.[1]?.trim();
  return code !== undefined && code in PLATFORM_NAMES ? code : undefined;
};

/**
 * What a device is called, when it called itself something.
 *
 * The label and platform are whatever the desktop recorded at activation; this
 * application never invents a name for a machine, and never fingerprints one to
 * guess at a better one. Dropping the platform the desktop wrote into the label
 * is not inventing one — it is the same fact, drawn once, in the row beneath.
 */
export const describeDeviceName = (device: Device, fallback: string) => {
  const label = device.label?.trim();
  if (!label) {
    return fallback;
  }
  const labelPlatform = readLabelPlatform(label);
  if (labelPlatform === undefined) {
    return label;
  }
  return label.replace(PLATFORM_LABEL_SUFFIX, "").trim() || fallback;
};

/**
 * The platform as a person would say it, or nothing when it was not recorded.
 *
 * Falls back to the code in the label, because that is where the name it was
 * taken from came from — dropping it from one place and not reading it in the
 * other would lose the fact rather than move it.
 */
export const describeDevicePlatform = (device: Device) => {
  const code = device.platform ?? readLabelPlatform(device.label?.trim() ?? "");
  if (code === undefined) {
    return undefined;
  }
  return PLATFORM_NAMES[code] ?? code;
};

/**
 * Formatted against the application's own language rather than
 * `toLocaleDateString()`'s default, which follows the operating system — a user
 * reading Lithuanian on an English machine would otherwise get every other date
 * in their language and these ones in somebody else's.
 */
export const formatActivationDate = (activatedAt: string, language: AppLanguage) => {
  const activated = new Date(activatedAt);
  if (Number.isNaN(activated.getTime())) {
    return undefined;
  }
  return new Intl.DateTimeFormat(language, { dateStyle: "medium" }).format(activated);
};
